import { Component, type ErrorInfo, type ReactNode } from 'react';

import { cn } from '../utils';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Fallback UI to show when an error occurs */
  fallback?: ReactNode;
  /** Custom error handler callback */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Optional className for the error container */
  className?: string;
  /** Whether to show a minimal error UI (for widgets/cards) vs full error UI */
  compact?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component to catch JavaScript errors in child components.
 * Prevents the entire app from crashing due to errors in isolated components.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Compact error UI for widgets/cards
      if (this.props.compact) {
        return (
          <div
            className={cn(
              'flex h-full flex-col items-center justify-center rounded-lg bg-red-50 p-4 dark:bg-red-500/10',
              this.props.className
            )}
          >
            <div className="mb-2 text-sm font-medium text-red-600 dark:text-red-400">
              Something went wrong
            </div>
            <button
              onClick={this.handleRetry}
              className="text-xs text-red-500 underline hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              Try again
            </button>
          </div>
        );
      }

      // Full error UI for pages/sections
      return (
        <div
          className={cn(
            'flex flex-col items-center justify-center rounded-xl border border-red-200 bg-red-50 p-8 dark:border-red-500/20 dark:bg-red-500/10',
            this.props.className
          )}
        >
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
            <svg
              className="h-6 w-6 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-red-800 dark:text-red-300">
            Something went wrong
          </h3>
          <p className="mb-4 max-w-md text-center text-sm text-red-600 dark:text-red-400">
            An error occurred while rendering this section. You can try again or refresh the page.
          </p>
          {this.state.error && (
            <details className="mb-4 w-full max-w-md">
              <summary className="cursor-pointer text-xs text-red-500 dark:text-red-400">
                Show error details
              </summary>
              <pre className="mt-2 overflow-auto rounded bg-red-100 p-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
                {this.state.error.message}
              </pre>
            </details>
          )}
          <div className="flex gap-3">
            <button
              onClick={this.handleRetry}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
